import {ChangeDetectorRef, Component, OnInit, ViewChild} from '@angular/core';
import {ConfigServiceProvider} from "../../services/config/config.service.provider";
import {ConfirmationService, MessageService} from "primeng/api";
import {UIChart} from "primeng/chart";
import {TranslateService} from "@ngx-translate/core";
import {CcBaseComponent} from "../../crystal-elements/cc-base.component";
import {MetricsService} from "../../services/metrics/metrics.service";
import {ClearingScorecard} from "../../model/metrics/clearing-scorecard";
import {RecordRequest} from "../../model/api/record-request";
import {SecurityType} from "../../model/metrics/security-type";
import {AppConfigService} from "../../services/app-config.service";
import {EMPTY, exhaustMap, takeUntil, timer} from "rxjs";
import {catchError} from "rxjs/operators";

@Component({
  selector: 'cc-cleared-uncleared-chart',
  templateUrl: './cleared-uncleared-chart.component.html',
  styleUrls: ['./cleared-uncleared-chart.component.scss'],
  providers: [
    ConfigServiceProvider, MessageService
  ]
})
export class ClearedUnclearedChartComponent extends CcBaseComponent implements OnInit {

  @ViewChild('chart') chartRef: UIChart;

  chartData: any;
  chartOptions: any;

  currentRequest: RecordRequest = {};

  metricsScores: ClearingScorecard[] = [];

  transPartKey: string = 'chart.clearedUncleared';

  private clearingStatusScoresLoopSub?: any;

  constructor(cdref: ChangeDetectorRef,
              private metricsService: MetricsService,
              private appConfigService: AppConfigService,
              messageService: MessageService,
              confirmationService: ConfirmationService,
              translateService: TranslateService) {
    super(cdref, messageService, translateService);
  }

  protected initConfigs() {
    super.initConfigs();
    this.buildChartOptions();
    this.refreshChart();
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    setTimeout(() => {
      this.startClearingStatusScoresLoop();
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  startClearingStatusScoresLoop(): void {
    const intervalMs = this.appConfigService.chartsReloadInterval;

    this.clearingStatusScoresLoopSub?.unsubscribe();

    this.clearingStatusScoresLoopSub = timer(0, intervalMs)
      .pipe(
        takeUntil(this.destroy$),
        exhaustMap(() =>
          this.metricsService.getClearingStatusList(this.currentRequest).pipe(
            catchError(err => {
              this.errorMessage(String(err));
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe(metricsResult => {
        if (!metricsResult.success) return;

        if (metricsResult.syncMetadata?.needSync) {
          if (JSON.stringify(this.metricsScores) !== JSON.stringify(metricsResult.records)) {
            this.metricsScores = metricsResult.records;
            this.refreshChart();
          }
        }

        this.currentRequest.syncMetadata = metricsResult.syncMetadata;
      });
  }

  private buildChartOptions() {
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      resizeDelay: 200,
      plugins: {
        legend: {
          labels: { color: '#dedede' }
        },
        title: {
          display: true,
          text: this.translateService.instant('page.' + this.transPartKey + '.title'),
          color: '#dedede'
        }
      },
      scales: {
        x: {
          ticks: { color: '#dedede' },
          grid: { display: false }
        },
        y: {
          ticks: { color: '#dedede' },
          grid: { color: 'rgba(255,255,255,0.1)' }
        }
      }
    };
  }

  private refreshChart() {
    const exchangeCodes = this.getEtdExchangeCodes();
    const clearedData = exchangeCodes.map(code => this.getClearedCount(code));
    const unclearedData = exchangeCodes.map(code => this.getUnclearedCount(code));

    if (!this.chartData) {
      this.chartData = {
        labels: exchangeCodes,
        datasets: [
          {
            label: this.translateService.instant('page.' + this.transPartKey + '.series.cleared'),
            data: clearedData,
            backgroundColor: 'rgba(75, 192, 110, 0.7)',
            borderColor: 'rgba(75, 192, 110, 1)',
            borderWidth: 1
          },
          {
            label: this.translateService.instant('page.' + this.transPartKey + '.series.uncleared'),
            data: unclearedData,
            backgroundColor: 'rgba(235, 90, 90, 0.7)',
            borderColor: 'rgba(235, 90, 90, 1)',
            borderWidth: 1
          }
        ]
      };
      return;
    }

    this.chartData.labels = exchangeCodes;
    this.chartData.datasets[0].data = clearedData;
    this.chartData.datasets[1].data = unclearedData;
    this.chartRef?.refresh();
  }

  private getEtdExchangeCodes(): string[] {
    const codes = new Set<string>();
    this.metricsScores
      .filter(value => value.type === SecurityType.TRADE && !this.isOtcOrStp(value.exchangeCode))
      .forEach(value => codes.add(value.exchangeCode));
    return Array.from(codes).sort();
  }

  private isOtcOrStp(exchangeCode: string): boolean {
    return !exchangeCode || exchangeCode.endsWith('_OTC') || exchangeCode.endsWith('_STP');
  }

  private getClearedCount(exchangeCode: string): number {
    return this.metricsScores
      .filter(value => value.exchangeCode === exchangeCode && value.type === SecurityType.TRADE)
      .reduce((sum, value) => sum + value.totalCleared, 0);
  }

  private getUnclearedCount(exchangeCode: string): number {
    return this.metricsScores
      .filter(value => value.exchangeCode === exchangeCode && value.type === SecurityType.TRADE)
      .reduce((sum, value) =>
        sum + value.totalWaiting + value.totalUnallocated + value.totalError
        + value.totalPending + value.totalGiveUp, 0);
  }
}
